import React, { useState } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useApp } from '../lib/AppContext';
import { useEffectiveUser } from '../hooks/useEffectiveUser';
import { BROAD_CATEGORIES } from '../lib/constants';
import { supabase } from '../lib/supabaseClient';
import { SongSubmissionInsert } from '../lib/types';
import Button from '../components/Button';
import Header from '../components/Header';
import SubmissionSuccessModal from '../components/SubmissionSuccessModal';

interface FormErrors {
  songTitle?: string;
  artist?: string;
  whyItMatters?: string;
  submittedEmail?: string;
  general?: string;
}

export default function SubmitSong() {
  const { navigateTo, navigateBack, songSubmissionFormData, updateSongSubmissionFormData, clearSongSubmissionFormData } = useApp();
  const { user } = useEffectiveUser();

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedSong, setSubmittedSong] = useState<{ title: string; artist: string } | null>(null);
  const [honeypot, setHoneypot] = useState('');

  const handleTagToggle = (tag: string) => {
    const tagList = songSubmissionFormData.tagList.includes(tag)
      ? songSubmissionFormData.tagList.filter(t => t !== tag)
      : [...songSubmissionFormData.tagList, tag];

    updateSongSubmissionFormData({ tagList });
  };

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    if (!email) return true; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    const trimmedTitle = songSubmissionFormData.songTitle.trim();
    const trimmedArtist = songSubmissionFormData.artist.trim();
    const trimmedWhyItMatters = songSubmissionFormData.whyItMatters.trim();
    const trimmedEmail = songSubmissionFormData.submittedEmail.trim();

    // Required fields validation
    if (!trimmedTitle) {
      newErrors.songTitle = 'Please fill in this field.';
    }

    if (!trimmedArtist) {
      newErrors.artist = 'Please fill in this field.';
    }

    if (!trimmedWhyItMatters) {
      newErrors.whyItMatters = 'Please tell us why this song matters.';
    } else if (trimmedWhyItMatters.length > 1000) {
      newErrors.whyItMatters = 'Must be less than 1000 characters.';
    }

    // Optional email validation
    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      newErrors.submittedEmail = 'Please enter a valid email address.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setErrors({});

    // Honeypot spam protection - if filled, reject silently
    if (honeypot) {
      console.log('Honeypot triggered - potential spam');
      setIsSubmitting(false);
      return;
    }

    // Validate form
    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare submission data for database
      const submissionData: SongSubmissionInsert = {
        song_title: songSubmissionFormData.songTitle.trim(),
        artist: songSubmissionFormData.artist.trim(),
        why_it_matters: songSubmissionFormData.whyItMatters.trim(),
        tag_list: songSubmissionFormData.tagList.length > 0 ? songSubmissionFormData.tagList : undefined,
        track_link: songSubmissionFormData.trackLink.trim() || undefined,
        submitted_name: songSubmissionFormData.submittedName.trim() || undefined,
        submitted_email: songSubmissionFormData.submittedEmail.trim() || undefined,
        user_id: user?.id || undefined,
      };

      // Insert submission into database
      const { data: submission, error: insertError } = await supabase
        .from('song_submissions')
        .insert(submissionData)
        .select()
        .single();

      if (insertError) {
        console.error('Error inserting submission:', insertError);
        setErrors({ general: 'Failed to submit. Please try again.' });
        setIsSubmitting(false);
        return;
      }

      console.log('Submission created:', submission);

      // Call Edge Function to send notification emails
      try {
        const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-new-submission`;
        const functionResponse = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify(submission),
        });

        if (!functionResponse.ok) {
          console.warn('Edge function returned error:', await functionResponse.text());
          // Don't fail the submission if email notification fails
        } else {
          const result = await functionResponse.json();
          console.log('Email notifications sent:', result);
        }
      } catch (emailError) {
        console.warn('Failed to send email notifications:', emailError);
        // Don't fail the submission if email notification fails
      }

      // Store submitted song info for success modal
      setSubmittedSong({
        title: submissionData.song_title,
        artist: submissionData.artist,
      });

      // Show success modal
      setShowSuccessModal(true);

      // Clear form data
      clearSongSubmissionFormData();

    } catch (error) {
      console.error('Unexpected error during submission:', error);
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close and navigation
  const handleModalClose = () => {
    setShowSuccessModal(false);
    navigateTo('home');
  };

  return (
    <div className="min-h-screen bg-[#111111]" style={{ paddingBottom: 'calc(92px + env(safe-area-inset-bottom, 0px) + 16px)' }}>
      <Header
        mode="title"
        title=""
        showBackButton
        backButtonLabel="Back"
        onBackClick={navigateBack}
      />

      <div className="w-full max-w-3xl mx-auto px-4 py-6 lg:py-8">
        <h1 className="text-3xl font-bold text-white mb-2" style={{ letterSpacing: '-0.005em' }}>Submit a Song</h1>
        <p className="text-[15px] text-gray-300 mb-8" style={{ lineHeight: '1.55' }}>
          Know a song with a story, message, or moment? Share it with us.
        </p>

        {errors.general && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
            <p className="text-red-400 text-sm">{errors.general}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="songTitle" className="block text-white font-medium mb-2" style={{ fontWeight: '500' }}>
              Song Title <span className="text-[#FF6F61]">*</span>
            </label>
            <input
              type="text"
              id="songTitle"
              value={songSubmissionFormData.songTitle}
              onChange={(e) => updateSongSubmissionFormData({ songTitle: e.target.value })}
              className={`w-full px-4 py-3 bg-[#111111] border ${
                errors.songTitle ? 'border-[#FF6F61]' : ''
              } rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-[#40E0D0] focus:ring-2 focus:ring-[#40E0D0]/20 transition-all`}
              style={!errors.songTitle ? { borderColor: 'rgba(255, 255, 255, 0.08)' } : undefined}
              placeholder="Enter song title"
            />
            {errors.songTitle && (
              <p className="text-[#FF6F61] text-sm mt-1">{errors.songTitle}</p>
            )}
          </div>

          <div>
            <label htmlFor="artist" className="block text-white font-medium mb-2" style={{ fontWeight: '500' }}>
              Artist <span className="text-[#FF6F61]">*</span>
            </label>
            <input
              type="text"
              id="artist"
              value={songSubmissionFormData.artist}
              onChange={(e) => updateSongSubmissionFormData({ artist: e.target.value })}
              className={`w-full px-4 py-3 bg-[#111111] border ${
                errors.artist ? 'border-[#FF6F61]' : ''
              } rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-[#40E0D0] focus:ring-2 focus:ring-[#40E0D0]/20 transition-all`}
              style={!errors.artist ? { borderColor: 'rgba(255, 255, 255, 0.08)' } : undefined}
              placeholder="Enter artist name"
            />
            {errors.artist && (
              <p className="text-[#FF6F61] text-sm mt-1">{errors.artist}</p>
            )}
          </div>

          <div>
            <label htmlFor="whyItMatters" className="block text-white font-medium mb-2" style={{ fontWeight: '500' }}>
              Tell us why this song matters <span className="text-[#FF6F61]">*</span>
            </label>
            <textarea
              id="whyItMatters"
              value={songSubmissionFormData.whyItMatters}
              onChange={(e) => updateSongSubmissionFormData({ whyItMatters: e.target.value })}
              rows={4}
              className={`w-full px-4 py-3 bg-[#111111] border ${
                errors.whyItMatters ? 'border-[#FF6F61]' : ''
              } rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-[#40E0D0] focus:ring-2 focus:ring-[#40E0D0]/20 transition-all resize-none`}
              style={!errors.whyItMatters ? { borderColor: 'rgba(255, 255, 255, 0.08)' } : undefined}
              placeholder="What issue does it touch? What story does it carry?"
            />
            {errors.whyItMatters && (
              <p className="text-[#FF6F61] text-sm mt-1">{errors.whyItMatters}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {songSubmissionFormData.whyItMatters.length}/1000 characters
            </p>
          </div>

          <div>
            <label className="block text-white font-medium mb-2" style={{ fontWeight: '500' }}>
              Topic Tags <span className="text-gray-500 text-sm font-normal">(optional)</span>
            </label>
            <p className="text-sm text-gray-400 mb-4" style={{ lineHeight: '1.5' }}>
              Select any that fit. You can choose more than one.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {BROAD_CATEGORIES.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleTagToggle(tag)}
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                    songSubmissionFormData.tagList.includes(tag)
                      ? 'bg-[#40E0D0]/15 text-white border-[#40E0D0]/40 border'
                      : 'bg-[#111111] text-gray-300 border hover:bg-[#1A1A1A] active:bg-[#0F0F0F]'
                  }`}
                  style={!songSubmissionFormData.tagList.includes(tag) ? { borderColor: 'rgba(255, 255, 255, 0.08)' } : undefined}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="trackLink" className="block text-white font-medium mb-2">
              Link to the song <span className="text-gray-500 text-sm font-normal">(optional)</span>
            </label>
            <input
              type="url"
              id="trackLink"
              value={songSubmissionFormData.trackLink}
              onChange={(e) => updateSongSubmissionFormData({ trackLink: e.target.value })}
              className="w-full px-4 py-3 bg-[#111111] border rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-[#40E0D0] focus:ring-2 focus:ring-[#40E0D0]/20 transition-all"
              style={{ borderColor: 'rgba(255, 255, 255, 0.08)' }}
              placeholder="Spotify/YouTube link"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="submittedName" className="block text-white font-medium mb-2">
                Your name <span className="text-gray-500 text-sm font-normal">(optional)</span>
              </label>
              <input
                type="text"
                id="submittedName"
                value={songSubmissionFormData.submittedName}
                onChange={(e) => updateSongSubmissionFormData({ submittedName: e.target.value })}
                className="w-full px-4 py-3 bg-[#111111] border rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-[#40E0D0] focus:ring-2 focus:ring-[#40E0D0]/20 transition-all"
                style={{ borderColor: 'rgba(255, 255, 255, 0.08)' }}
                placeholder="Optional"
              />
            </div>

            <div>
              <label htmlFor="submittedEmail" className="block text-white font-medium mb-2">
                Email <span className="text-gray-500 text-sm font-normal">(optional)</span>
              </label>
              <input
                type="email"
                id="submittedEmail"
                value={songSubmissionFormData.submittedEmail}
                onChange={(e) => updateSongSubmissionFormData({ submittedEmail: e.target.value })}
                className={`w-full px-4 py-3 bg-[#111111] border ${
                  errors.submittedEmail ? 'border-[#FF6F61]' : ''
                } rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-[#40E0D0] focus:ring-2 focus:ring-[#40E0D0]/20 transition-all`}
                style={!errors.submittedEmail ? { borderColor: 'rgba(255, 255, 255, 0.08)' } : undefined}
                placeholder="Optional"
              />
              {errors.submittedEmail && (
                <p className="text-[#FF6F61] text-sm mt-1">{errors.submittedEmail}</p>
              )}
            </div>
          </div>

          {/* Honeypot field - hidden from users, catches bots */}
          <input
            type="text"
            name="website"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />

          <div className="pt-4">
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Song
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </Button>
          </div>
        </form>
      </div>

      {/* Success Modal */}
      {submittedSong && (
        <SubmissionSuccessModal
          isOpen={showSuccessModal}
          onClose={handleModalClose}
          songTitle={submittedSong.title}
          artist={submittedSong.artist}
        />
      )}
    </div>
  );
}
